/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: conferencesActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Applications module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../config/api';

/**
 * Shared helpers for Conferences module actions (used in Grid + Form views).
 */

/**
 * Build the URL used to duplicate a conference in the form.
 * Pattern: /pbx/applications/conferences/new?duplicate={id}
 */
export const getConferenceDuplicateUrl = (id: string | number): string =>
  `/pbx/applications/conferences/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given conference id.
 */
export const navigateToConferenceDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getConferenceDuplicateUrl(id));
};

/**
 * Low-level API helper to delete a conference by id.
 * You can override headers (for multi-tenant/user-scoped requests); otherwise default API headers are used.
 */
export const deleteConferenceById = async (
  id: string | number,
  headersOverride?: HeadersInit,
): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`/api/telephony/conferences/${id}`),
    {
      method: 'DELETE',
      headers: headersOverride || getApiHeaders(),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete conference';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};


